import { useState } from "react";
import { startSamlSignIn } from "@/lib/auth";
import styles from "./SamlSignInButton.module.css";

interface SamlSignInButtonProps {
  /** Cognito IdP provider name from the public SAML lookup. */
  providerName: string;
  /** Where to return after the callback completes. */
  next: string;
}

/**
 * "Continue with SSO" button rendered on `/signin/:wsId` when the
 * workspace has SAML SSO configured. Reuses the same PKCE Hosted UI
 * flow as Google sign-in — just with a different `identity_provider`
 * query param. Disables itself while the redirect kicks off so a
 * double-click doesn't fire two authorize requests.
 */
export function SamlSignInButton({ providerName, next }: SamlSignInButtonProps) {
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onClick() {
    if (pending) return;
    setErr(null);
    setPending(true);
    try {
      await startSamlSignIn(providerName, next);
      // startSamlSignIn assigns window.location; we won't reach here on
      // success. If the browser blocks the navigation, drop the pending
      // state so the user can retry.
    } catch (e) {
      setPending(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.btn}
        onClick={onClick}
        disabled={pending}
        aria-disabled={pending}
      >
        <ShieldLogo />
        <span className={styles.label}>
          {pending ? "Redirecting to your IdP…" : "Continue with SSO"}
        </span>
      </button>
      {err && (
        <p className={styles.err} role="alert">
          {err}
        </p>
      )}
    </div>
  );
}

function ShieldLogo() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
